import { reportRecordSchema, runningReportResponseSchema } from "../../shared/schemas/report.js";
import type { Publication, ScoreReading } from "../../shared/types/activate.js";
import type { ContextEngineView, EngineClaimResult } from "../../shared/schemas/contextEngine.js";
import type { Archetype, HumanAssertion, ReportRecord, ScanDepth } from "../../shared/types/index.js";

const POLL_INTERVAL_MS = 1_500;
const POLL_TIMEOUT_MS = 180_000;

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly code?: string,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

async function requestJson(input: string, init?: RequestInit): Promise<{ status: number; body: unknown }> {
  const response = await fetch(input, init);
  const body = await response.json().catch(() => null);
  if (!response.ok) {
    const record = body && typeof body === "object" ? (body as Record<string, unknown>) : {};
    const message = typeof record.message === "string" ? record.message : `Request failed (${response.status})`;
    throw new ApiError(message, response.status, typeof record.error === "string" ? record.error : undefined);
  }
  return { status: response.status, body };
}

export interface CreateReportOptions {
  archetype?: Archetype;
  fixtureId?: string;
  /** Accepted and ignored: there is one scan. Kept so an older caller's request still parses. */
  depth?: ScanDepth;
  /** An address the finished report is sent to. Delivery is all it buys. */
  email?: string;
  /** Which surface asked, so the page's form and an agent driving the page stay distinguishable. */
  surface?: "web" | "webmcp";
  /** Read the site again even if it was read in the last day; the explicit re-verify. */
  fresh?: boolean;
  requestId?: string;
  signal?: AbortSignal;
  /** Overridable so tests do not wait on real timers. */
  waitMs?: (ms: number) => Promise<void>;
  now?: () => number;
}

const defaultWait = (ms: number) => new Promise<void>((resolve) => window.setTimeout(resolve, ms));

/** The POST alone. A refusal — a rate limit, a rejected address, an unsafe URL — surfaces here. */
async function postReport(url: string, requestId: string, options: CreateReportOptions) {
  return requestJson("/api/reports", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({
      requestId,
      url,
      archetypeOverride: options.archetype ?? null,
      fixtureId: options.fixtureId ?? null,
      ...(options.depth ? { depth: options.depth } : {}),
      ...(options.email ? { email: options.email } : {}),
      ...(options.surface ? { surface: options.surface } : {}),
      ...(options.fresh ? { fresh: true } : {}),
    }),
    signal: options.signal,
  });
}

async function terminalFrom(
  accepted: { status: number; body: unknown },
  options: CreateReportOptions,
): Promise<ReportRecord> {
  if (accepted.status === 202) {
    const running = runningReportResponseSchema.parse(accepted.body);
    return waitForTerminalReport(running.reportId, options);
  }
  const report = reportRecordSchema.parse(accepted.body);
  return report.status === "running" ? waitForTerminalReport(report.id, options) : report;
}

/**
 * Creates a report and resolves only once a terminal revision exists. A 202 means the same
 * requestId is still running, so the caller recovers the stored state by polling rather than
 * reporting "audit started" as a result.
 */
export async function createReport(url: string, options: CreateReportOptions = {}): Promise<ReportRecord> {
  const requestId = options.requestId ?? crypto.randomUUID();
  return terminalFrom(await postReport(url, requestId, options), options);
}

export interface StartedReport {
  /** The report id is the caller's requestId, so the page exists as soon as the record does. */
  reportId: string;
  /**
   * Resolves when the service has accepted the audit, and rejects with its refusal. A caller that
   * only watches `ready` cannot tell "started" from "refused" until a minute later, which is how a
   * rate limit or a rejected address ends up announced as success.
   */
  accepted: Promise<void>;
  ready: Promise<ReportRecord>;
}

/**
 * Starts an audit and returns immediately with the id. `ready` resolves at the terminal report;
 * the caller can navigate to /reports/{reportId} right away and watch the record fill in.
 */
export function startReport(url: string, options: CreateReportOptions = {}): StartedReport {
  const requestId = options.requestId ?? crypto.randomUUID();
  const posted = postReport(url, requestId, options);
  const accepted = posted.then(() => undefined);
  const ready = posted.then((response) => terminalFrom(response, options));
  // Two promises come off one request, and most callers want only one of them. Marking both as
  // observed keeps the one nobody awaited from surfacing as an unhandled rejection; each still
  // rejects for whoever does await it.
  accepted.catch(() => undefined);
  ready.catch(() => undefined);
  return { reportId: requestId, accepted, ready };
}

export async function waitForTerminalReport(reportId: string, options: CreateReportOptions = {}): Promise<ReportRecord> {
  const wait = options.waitMs ?? defaultWait;
  const now = options.now ?? (() => Date.now());
  const deadline = now() + POLL_TIMEOUT_MS;

  for (;;) {
    const report = await getReport(reportId);
    if (report.status !== "running") return report;
    if (now() >= deadline) {
      throw new ApiError("The audit is taking longer than expected. Open the report link to check again.", 504);
    }
    await wait(POLL_INTERVAL_MS);
  }
}

/**
 * "Send me the report": the address, given while the audit runs or after it landed. The report
 * itself never changes; a report that already landed is sent at once.
 */
export async function requestDelivery(
  reportId: string,
  email: string,
  surface: "web" | "webmcp" = "web",
): Promise<{ reportId: string; maskedEmail: string | null; status: string }> {
  const { body } = await requestJson(`/api/reports/${reportId}/deliver`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ email, surface }),
  });
  return body as { reportId: string; maskedEmail: string | null; status: string };
}

export async function getReport(reportId: string): Promise<ReportRecord> {
  const { body } = await requestJson(`/api/reports/${reportId}`);
  return reportRecordSchema.parse(body);
}

export async function getPinnedAlpinaReport(): Promise<ReportRecord> {
  const { body } = await requestJson("/api/demo/alpina");
  return reportRecordSchema.parse(body);
}

/** Asks the foundation audit again for a report that landed without it; answers with the new version that carries it. */
export async function retryFoundation(reportId: string): Promise<ReportRecord> {
  const { body } = await requestJson(`/api/reports/${reportId}/foundation`, { method: "POST" });
  return reportRecordSchema.parse(body);
}

export async function recompileReport(reportId: string, archetype: Archetype): Promise<ReportRecord> {
  const { body } = await requestJson(`/api/reports/${reportId}/recompile`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ archetype }),
  });
  return reportRecordSchema.parse(body);
}

/**
 * Applies a reviewer's structured judgment as an immutable child revision. When this browser holds
 * the site's Context Engine, the key travels with it and the engine keeps the decisions too.
 */
export async function refineReport(reportId: string, assertions: HumanAssertion, engineKey?: string | null): Promise<ReportRecord> {
  const { body } = await requestJson(`/api/reports/${reportId}/refine`, {
    method: "POST",
    headers: { "content-type": "application/json", ...(engineKey ? { [ENGINE_KEY_HEADER]: engineKey } : {}) },
    body: JSON.stringify(assertions),
  });
  return reportRecordSchema.parse(body);
}

const ENGINE_KEY_HEADER = "x-context-engine-key";
const withKey = (key: string | null | undefined): Record<string, string> => (key ? { [ENGINE_KEY_HEADER]: key } : {});

/** The site's Context Engine as anyone may read it, and what this browser's key stands for on it. */
export async function getEngine(reportId: string, key?: string | null): Promise<EngineWithStanding> {
  const { body } = await requestJson(`/api/engines/for-report/${reportId}`, { headers: withKey(key) });
  // A proxy or a page under test may answer with something else; that is no engine.
  const engine = body as Partial<EngineWithStanding> | null;
  if (!engine || typeof engine.host !== "string" || !engine.owner || !Array.isArray(engine.snapshots) || !engine.decisions) throw new Error("Not a Context Engine");
  return engine as EngineWithStanding;
}

export async function claimEngine(reportId: string): Promise<EngineClaimResult> {
  const { body } = await requestJson(`/api/engines/for-report/${reportId}/claim`, { method: "POST" });
  return body as EngineClaimResult;
}

export async function getEngineVerification(host: string, key: string): Promise<EngineVerification> {
  const { body } = await requestJson(`/api/engines/${encodeURIComponent(host)}/verification`, { headers: withKey(key) });
  return body as EngineVerification;
}

export async function verifyEngine(host: string, key: string): Promise<ContextEngineView> {
  const { body } = await requestJson(`/api/engines/${encodeURIComponent(host)}/verify`, { method: "POST", headers: withKey(key) });
  return body as ContextEngineView;
}

export async function getReviewToken(host: string, key: string): Promise<{ token: string; expiresAt: string }> {
  const { body } = await requestJson(`/api/engines/${encodeURIComponent(host)}/review-token`, { method: "POST", headers: withKey(key) });
  return body as { token: string; expiresAt: string };
}

export type EngineStanding = "none" | "pending" | "reviewer" | "owner";
export type EngineWithStanding = ContextEngineView & { standing: EngineStanding };
export interface EngineVerification {
  code: string;
  metaTag: string;
  wellKnownUrl: string;
}

export interface AlpinaAvailabilityInput {
  /** Who is asking: a person on the page, or an agent in the page. Attribution, not authorization. */
  surface?: "web" | "webmcp";
  reportId?: string;
  propertyId?: string;
  checkIn: string;
  checkOut: string;
  adults: number;
  childrenAges?: number[];
  locale?: "en" | "de" | "it";
}

export interface AlpinaAvailabilityResponse {
  source: string;
  propertyId: string;
  available: boolean;
  status: "available" | "unavailable" | "unknown";
  checkIn: string;
  checkOut: string;
  nights: number;
  adults: number;
  childrenAges: number[];
  totalGuests: number;
  quote?: { total: number; currency: string; instantConfirmation?: boolean; cancellationSummary?: string; taxes?: string[] };
  checkoutUrl?: string;
  checkedAt: string;
  expiresAt?: string;
  requiresRevalidation: boolean;
  readOnly: true;
  notice: string;
  entity?: { id: string; type: string; name: string; sourceUrl: string; method: string; collectedAt: string };
  updatedReportId?: string;
  updatedReportUrl?: string;
  reportUpdateError?: string;
}

/** What a person can call on one capability, live from the site's own server, each with its input schema and whether it is safe to call. */
export interface TestableInterface {
  id: string;
  name: string;
  protocol: "mcp" | "sidecar" | "http-get";
  endpoint: string;
  safe: boolean;
  note?: string;
  description?: string;
  inputSchema?: Record<string, unknown>;
}

export interface CapabilityTestOutcome {
  /** "errored": the server answered, with an error of its own. "failed": no answer came. */
  outcome: "answered" | "errored" | "failed";
  latencyMs: number;
  answer: string;
  error?: string;
  request: { endpoint: string; tool: string; arguments: Record<string, unknown> };
  testedAt: string;
  updatedReportId?: string;
  updatedReportUrl?: string;
}

export async function prepareCapabilityTest(reportId: string, actionId: string): Promise<{ interfaces: TestableInterface[] }> {
  const { body } = await requestJson(`/api/reports/${reportId}/capabilities/${encodeURIComponent(actionId)}/test`);
  return body as { interfaces: TestableInterface[] };
}

export async function runCapabilityTest(reportId: string, actionId: string, input: { interfaceId: string; arguments: Record<string, unknown>; save?: boolean }): Promise<CapabilityTestOutcome> {
  const { body } = await requestJson(`/api/reports/${reportId}/capabilities/${encodeURIComponent(actionId)}/test`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(input),
  });
  return body as CapabilityTestOutcome;
}

/** Read-only availability lookup through the approved server-side sidecar. */
export async function checkAlpinaAvailability(input: AlpinaAvailabilityInput): Promise<AlpinaAvailabilityResponse> {
  const { body } = await requestJson("/api/sidecars/alpina/availability", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(input),
  });
  return body as AlpinaAvailabilityResponse;
}

export interface ReportVisits {
  reportId: string;
  since: string;
  days: Array<{ day: string; counts: Record<string, number> }>;
  activations: Array<{ day: string; tool: string; surface: string; outcome: string; count: number }>;
  /** The site's readiness readings still in the store, newest first. */
  history?: ScoreReading[];
}

/** What the site publishes from a report: one model, three documents. */
export async function getPublication(reportId: string): Promise<Publication> {
  const { body } = await requestJson(`/api/reports/${reportId}/publish`, { method: "GET" });
  if (!body || typeof body !== "object" || !Array.isArray((body as Publication).actions)) throw new Error("Not a publication");
  return body as Publication;
}

/** Who has read this report, by class and by day. Counts only. */
export async function getVisits(reportId: string): Promise<ReportVisits> {
  const { body } = await requestJson(`/api/reports/${reportId}/visits`, { method: "GET" });
  // A page under test, or a proxy, may answer with something else; the line is then left out.
  if (!body || typeof body !== "object" || !Array.isArray((body as ReportVisits).days)) throw new Error("Not a visits ledger");
  return body as ReportVisits;
}

export function contractPath(reportId: string, actionId: string): string {
  return `/api/reports/${reportId}/contracts/${encodeURIComponent(actionId)}`;
}

export function absoluteUrl(path: string): string {
  if (typeof window === "undefined") return path;
  return new URL(path, window.location.origin).toString();
}

export function reportPageUrl(reportId: string): string {
  return absoluteUrl(`/reports/${reportId}`);
}
